import type { KickFlowConfig } from '../types';
import { createKickError, KickNetworkError } from './errors';
import { DebugLogger } from './logger';
import { OAuthManager } from './oauth-manager';

/**
 * HTTP client for making authenticated requests to the Kick API
 */
export class HttpClient {
	private readonly logger: DebugLogger;

	constructor(
		private config: KickFlowConfig,
		private oauthManager: OAuthManager
	) {
		this.logger = new DebugLogger(config.debug);
	}

	/**
	 * Make an authenticated HTTP request to the Kick API
	 * 
	 * @param endpoint - API endpoint (without base URL)
	 * @param options - Fetch options
	 * @returns Promise resolving to response data
	 */
	async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
		try {
			const accessToken = await this.oauthManager.getAccessToken();
			const url = `${this.config.baseUrl}${endpoint}`;

			// Prepare request options with authentication
			const requestOptions: RequestInit = {
				...options,
				headers: {
					Authorization: `Bearer ${accessToken}`,
					Accept: 'application/json',
					'Content-Type': 'application/json',
					...options.headers,
				},
			};

			this.logger.apiRequest(url, requestOptions);

			const response = await fetch(url, requestOptions);

			if (!response.ok) {
				await this.handleErrorResponse(response, endpoint);
			}

			const json = await response.json();
			return json.data || json;
		} catch (error) {
			if (error instanceof Error && error.constructor.name.startsWith('Kick')) {
				throw error;
			}

			throw new KickNetworkError(
				`Request to ${endpoint} failed`,
				error as Error
			);
		}
	}

	/**
	 * Make a GET request
	 * 
	 * @param endpoint - API endpoint
	 * @param params - Query parameters
	 * @returns Promise resolving to response data
	 */
	async get<T>(endpoint: string, params?: Record<string, string | number>): Promise<T> {
		let url = endpoint;
		
		if (params) {
			const searchParams = new URLSearchParams();
			Object.entries(params).forEach(([key, value]) => {
				searchParams.append(key, String(value));
			});
			url += `?${searchParams.toString()}`;
		}

		return this.request<T>(url);
	}

	/**
	 * Make a POST request
	 * 
	 * @param endpoint - API endpoint
	 * @param data - Request body data
	 * @returns Promise resolving to response data
	 */
	async post<T>(endpoint: string, data?: unknown): Promise<T> {
		return this.request<T>(endpoint, {
			method: 'POST',
			body: data ? JSON.stringify(data) : undefined,
		});
	}

	/**
	 * Make a PATCH request
	 * 
	 * @param endpoint - API endpoint
	 * @param data - Request body data
	 * @returns Promise resolving to response data
	 */
	async patch<T>(endpoint: string, data?: unknown): Promise<T> {
		return this.request<T>(endpoint, {
			method: 'PATCH',
			body: data ? JSON.stringify(data) : undefined,
		});
	}

	/**
	 * Make a PUT request
	 * 
	 * @param endpoint - API endpoint
	 * @param data - Request body data
	 * @returns Promise resolving to response data
	 */
	async put<T>(endpoint: string, data?: unknown): Promise<T> {
		return this.request<T>(endpoint, {
			method: 'PUT',
			body: data ? JSON.stringify(data) : undefined,
		});
	}

	/**
	 * Make a DELETE request
	 * 
	 * @param endpoint - API endpoint
	 * @returns Promise resolving to response data
	 */
	async delete<T>(endpoint: string): Promise<T> {
		return this.request<T>(endpoint, {
			method: 'DELETE',
		});
	}

	/**
	 * Handle error responses from the API
	 */
	private async handleErrorResponse(response: Response, endpoint: string): Promise<void> {
		let responseBody: unknown;
		try {
			const text = await response.text();
			try {
				responseBody = JSON.parse(text);
			} catch {
				responseBody = text;
			}
		} catch {
			responseBody = 'Unable to read response body';
		}

		// Convert headers to plain object
		const headers: Record<string, string> = {};
		response.headers.forEach((value, key) => {
			headers[key] = value;
		});

		this.logger.apiError(
			response.status,
			response.statusText,
			headers,
			responseBody,
			endpoint
		);

		throw createKickError(
			response.status,
			response.statusText,
			responseBody,
			headers,
			endpoint
		);
	}
}
